import React from "react";
import { HStack, Link } from "@chakra-ui/react";

/**
 * Visual variant for ArtifactLink.
 *
 * "link"  — inline blue hyperlink with underline-on-hover (used in markdown prose).
 * "chip"  — borderless button chip with opacity-on-hover (used in ToolRunRow output lists).
 */
export type ArtifactLinkVariant = "link" | "chip";

/**
 * Shared artifact reference component used by both MarkdownRenderer and ToolRunRow.
 * Click logic is identical across variants — only visual style differs.
 *
 * - isActive:  when false the element renders non-interactive (artifact not found).
 * - onClick:   called when the user activates the link (click or keyboard Enter/Space).
 * - variant:   "link" (default) or "chip".
 * - children:  icon + label content supplied by the caller.
 */
export const ArtifactLink: React.FC<{
  isActive: boolean;
  onClick: () => void;
  variant?: ArtifactLinkVariant;
  children: React.ReactNode;
}> = ({ isActive, onClick, variant = "link", children }) => {
  if (variant === "chip") {
    return (
      <HStack
        as="button"
        type="button"
        spacing={1}
        align="center"
        cursor={isActive ? "pointer" : "default"}
        border="none"
        bg="transparent"
        p={0}
        onClick={e => {
          (e as React.MouseEvent).stopPropagation();
          if (isActive) onClick();
        }}
        _hover={isActive ? { opacity: 0.65 } : undefined}
      >
        {children}
      </HStack>
    );
  }

  return (
    <Link
      href="#"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        if (isActive) onClick();
      }}
      style={{
        color: "var(--chakra-colors-blue-400)",
        cursor: isActive ? "pointer" : "default",
        textDecoration: "none",
        fontWeight: 650,
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
        gap: "0.25rem",
      }}
      onMouseEnter={e => {
        if (isActive) e.currentTarget.style.textDecoration = "underline";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.textDecoration = "none";
      }}
    >
      {children}
    </Link>
  );
};
